"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";

type CookieCategoryKey = "analytics" | "marketing";

export interface CookieConsentSettings {
  id: string;
  isEnabled: boolean;
  enableLogging: boolean;
  layoutType: "cloud" | "bar";
  positionX: "left" | "center" | "right";
  positionY: "top" | "bottom";
  autoHideDelay: number | null;
  revision: number;
  updatedAt: string;
  content: {
    title: string;
    description: string;
    buttonLabels: {
      acceptAll: string;
      rejectAll: string;
      customize: string;
      savePreferences: string;
    };
    categories: {
      necessary: { enabled: boolean; label: string; description: string; readOnly?: boolean };
      analytics: { enabled: boolean; label: string; description: string; readOnly?: boolean };
      marketing: { enabled: boolean; label: string; description: string; readOnly?: boolean };
    };
  };
}

export interface CookieConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  action?: "accept_all" | "reject_all" | "customize";
  revision?: number;
  source?: string;
}

interface CookieConsentContextValue {
  ready: boolean;
  settings: CookieConsentSettings | null;
  preferences: CookieConsentPreferences | null;
  updatePreferences: (preferences: CookieConsentPreferences | null) => void;
  refresh: () => Promise<void>;
  canRunCategory: (category: CookieCategoryKey) => boolean;
}

const COOKIE_CONSENT_STORAGE_KEY = "notifystudio-cookie-consent-state";
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://admin.notifystudio.com/api/public";

const CookieConsentContext = createContext<CookieConsentContextValue>({
  ready: false,
  settings: null,
  preferences: null,
  updatePreferences: () => undefined,
  refresh: async () => undefined,
  canRunCategory: () => false,
});

function normalizeStoredPreferences(value: unknown): CookieConsentPreferences | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const source = value as Partial<CookieConsentPreferences>;
  return {
    necessary: true,
    analytics: source.analytics === true,
    marketing: source.marketing === true,
    action: source.action === "accept_all" || source.action === "reject_all" || source.action === "customize" ? source.action : undefined,
    revision: typeof source.revision === "number" && Number.isFinite(source.revision) ? source.revision : undefined,
    source: typeof source.source === "string" && source.source.trim() ? source.source.trim() : undefined,
  };
}

function readStoredPreferences() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeStoredPreferences(JSON.parse(raw));
  } catch {
    return null;
  }
}

function persistPreferences(preferences: CookieConsentPreferences | null) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!preferences) {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
  }
}

async function fetchCookieConsentSettings() {
  const response = await fetch(`${API_BASE}/cookie-consent`, { cache: "no-store" });
  if (!response.ok) {
    throw new Error("Failed to fetch cookie consent settings.");
  }

  return response.json() as Promise<CookieConsentSettings>;
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [settings, setSettings] = useState<CookieConsentSettings | null>(null);
  const [preferences, setPreferences] = useState<CookieConsentPreferences | null>(null);

  const updatePreferences = useCallback((next: CookieConsentPreferences | null) => {
    setPreferences(next);
    persistPreferences(next);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const nextSettings = await fetchCookieConsentSettings();
      setSettings(nextSettings);
      const storedPreferences = readStoredPreferences();
      if (storedPreferences && storedPreferences.revision === nextSettings.revision) {
        setPreferences(storedPreferences);
      } else {
        updatePreferences(null);
      }
    } catch {
      setSettings(null);
    } finally {
      setReady(true);
    }
  }, [updatePreferences]);

  useEffect(() => {
    void refresh();
  }, []);

  const canRunCategory = useCallback((category: CookieCategoryKey) => {
    if (!settings || !settings.isEnabled) {
      return true;
    }

    if (!settings.content.categories[category].enabled) {
      return false;
    }

    return preferences?.[category] === true;
  }, [preferences, settings]);

  return (
    <CookieConsentContext.Provider value={{ ready, settings, preferences, updatePreferences, refresh, canRunCategory }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}